import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import TopBar from '@/components/feature/TopBar';
import ActionBar from '@/components/feature/ActionBar';
import { useStore } from '@/hooks/useStore';

function fmtPhone(v: string) {
  const d = v.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return v;
}

export default function ClientesPage() {
  const { clientes, vendas, addCliente, updateCliente, deleteCliente } = useStore();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ nome: '', telefone: '' });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const filtered = clientes.filter(c =>
    c.nome.toLowerCase().includes(search.toLowerCase()) ||
    c.telefone.includes(search.replace(/\D/g, ''))
  );

  // ─── Métricas de clientes ──────────────────────────────────────────────
  const vendasComCliente = vendas.filter(v => v.clienteId);
  const totalVendasClientes = vendasComCliente.reduce((s, v) => s + v.valorLiquido, 0);
  const clientesAtivos = new Set(vendasComCliente.map(v => v.clienteId)).size;
  const ticketMedio = clientesAtivos > 0 ? totalVendasClientes / clientesAtivos : 0;

  const gastoPorCliente: Record<string, number> = {};
  vendasComCliente.forEach(v => {
    if (v.clienteId) {
      gastoPorCliente[v.clienteId] = (gastoPorCliente[v.clienteId] ?? 0) + v.valorLiquido;
    }
  });
  const melhorClienteId = Object.entries(gastoPorCliente).sort((a, b) => b[1] - a[1])[0]?.[0];
  const melhorCliente = clientes.find(c => c.id === melhorClienteId);

  function openNew() {
    setEditingId(null);
    setForm({ nome: '', telefone: '' });
    setShowModal(true);
  }

  function openEdit(c: typeof clientes[0]) {
    setEditingId(c.id);
    setForm({ nome: c.nome, telefone: c.telefone });
    setShowModal(true);
  }

  function handleSave() {
    if (!form.nome.trim()) return;
    if (editingId) {
      updateCliente(editingId, { nome: form.nome.trim(), telefone: form.telefone.trim() });
    } else {
      addCliente({ nome: form.nome.trim(), telefone: form.telefone.trim() });
    }
    setShowModal(false);
    setEditingId(null);
    setForm({ nome: '', telefone: '' });
  }

  return (
    <AppLayout>
      <TopBar title="Clientes" subtitle="Cadastre e gerencie seus clientes" />
      <ActionBar>
        <div className="relative w-full md:w-auto flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-sm text-dark-muted"></i>
          </div>
          <input
            type="text"
            placeholder="Buscar cliente..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input-dark pl-10 w-full"
          />
        </div>
        <button onClick={openNew} className="btn-gold w-full flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-xl cursor-pointer whitespace-nowrap">
          <i className="ri-add-line text-base"></i> Novo Cliente
        </button>
      </ActionBar>

      {/* ─── Métricas dos Clientes ───────────────────────────── */}
      <div className="pt-[196px] md:pt-[196px] p-3 md:p-6 pb-2 animate-fade-in-up">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {/* Card 1 — Clientes Ativos */}
          <div className="bg-dark-card border border-dark-border rounded-xl p-4 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-dark-muted mb-1">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-neon/10">
                <i className="ri-user-follow-line text-sm text-neon"></i>
              </div>
              <span className="text-xs font-medium">Clientes Ativos</span>
            </div>
            <span className="text-2xl font-bold text-dark-text">{clientesAtivos}</span>
            <span className="text-[11px] text-dark-muted">
              de {clientes.length} cadastrados
            </span>
          </div>

          {/* Card 2 — Ticket Médio */}
          <div className="bg-dark-card border border-dark-border rounded-xl p-4 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-dark-muted mb-1">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-neon/10">
                <i className="ri-coins-line text-sm text-neon"></i>
              </div>
              <span className="text-xs font-medium">Ticket Médio</span>
            </div>
            <span className="text-2xl font-bold text-dark-text">
              R$ {ticketMedio.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[11px] text-dark-muted">
              por cliente ativo
            </span>
          </div>

          {/* Card 3 — Total Vendido p/ Clientes */}
          <div className="bg-dark-card border border-dark-border rounded-xl p-4 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-dark-muted mb-1">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-neon/10">
                <i className="ri-shopping-bag-3-line text-sm text-neon"></i>
              </div>
              <span className="text-xs font-medium">Total Vendido</span>
            </div>
            <span className="text-2xl font-bold text-dark-text">
              R$ {totalVendasClientes.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[11px] text-dark-muted">
              {vendasComCliente.length} vendas com cliente
            </span>
          </div>

          {/* Card 4 — Melhor Cliente */}
          <div className="bg-dark-card border border-dark-border rounded-xl p-4 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-dark-muted mb-1">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-neon/10">
                <i className="ri-vip-crown-line text-sm text-neon"></i>
              </div>
              <span className="text-xs font-medium">Melhor Cliente</span>
            </div>
            <span className="text-lg font-bold text-dark-text truncate">
              {melhorCliente?.nome ?? '—'}
            </span>
            <span className="text-[11px] text-dark-muted">
              {melhorCliente
                ? `R$ ${gastoPorCliente[melhorClienteId!].toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : 'Nenhuma venda'}
            </span>
          </div>
        </div>
      </div>

      {/* ─── Tabela de Clientes ───────────────────────────────────── */}
      <div className="p-3 md:p-6 pt-2 md:pt-2 animate-fade-in-up">
        <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-10 flex flex-col items-center text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-dark-card2 mb-3">
                <i className="ri-user-3-line text-2xl text-dark-muted"></i>
              </div>
              <p className="text-sm text-dark-muted">
                {search ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
              <thead className="bg-dark-card2 border-b border-dark-border">
                <tr>
                  <th className="text-left text-xs text-dark-muted uppercase tracking-wider px-4 py-3">Nome</th>
                  <th className="text-left text-xs text-dark-muted uppercase tracking-wider px-4 py-3">Telefone</th>
                  <th className="text-right text-xs text-dark-muted uppercase tracking-wider px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {filtered.map(c => (
                  <tr key={c.id} className="hover:bg-dark-card2/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-neon/10 border border-neon/20 shrink-0">
                          <i className="ri-user-3-line text-sm text-neon"></i>
                        </div>
                        <span className="font-medium text-dark-text text-sm">{c.nome}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-dark-muted">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-phone-line text-xs"></i>
                        </div>
                        {c.telefone ? fmtPhone(c.telefone) : <span className="italic opacity-50">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(c)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted hover:text-neon cursor-pointer">
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button onClick={() => setConfirmDelete(c.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-500/10 text-dark-muted hover:text-red-400 cursor-pointer">
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-neon/15 rounded-2xl w-full max-w-sm p-5 animate-fade-in-up shadow-[0_0_40px_rgba(201,168,76,0.07)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-dark-text">{editingId ? 'Editar Cliente' : 'Novo Cliente'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Nome completo</label>
                <input
                  type="text"
                  className="input-dark"
                  placeholder="Ex: Maria Silva"
                  value={form.nome}
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Telefone</label>
                <input
                  type="tel"
                  className="input-dark"
                  placeholder="(11) 98765-4321"
                  value={form.telefone}
                  onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm font-semibold cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button onClick={handleSave} className="btn-gold flex-1 py-3 rounded-xl text-sm cursor-pointer whitespace-nowrap">
                {editingId ? 'Salvar' : 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-sm p-6 animate-fade-in-up">
            <h3 className="text-center text-base font-bold text-dark-text mb-2">Excluir cliente?</h3>
            <p className="text-center text-sm text-dark-muted mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => { deleteCliente(confirmDelete); setConfirmDelete(null); }} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}